import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import Button from "../common/Button";
import OnboardContext from "./context";
import { toPng } from "html-to-image";
import { langComponent } from "../../constants";
import AppContext from "../../AppContext";

import { ReactComponent as ArrowLeftSvg } from "../../assets/icons/arrow-left.svg";
import { ReactComponent as ArrowRightDisabledSvg } from "../../assets/icons/arrow-right-disabled.svg";
import { ReactComponent as ArrowRightSvg } from "../../assets/icons/arrow-right.svg";
import { ReactComponent as UploadSvg } from "../../assets/icons/upload.svg";
import FileUploader from "../common/FileUploader";

const Avatar = () => {
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.onboard);
  const TranslationCommon = getLang(langComponent.common);
  const {
    currentPlan,
    selectedPlan,
    goBack,
    goForward,
    addInformation,
    information: { info, avatar, companyLogo, credentials },
  } = useContext(OnboardContext);
  const [currentAvatar, setCurrentAvatar] = useState(
    avatar ??
      credentials?.googleAvatar ??
      credentials?.lineAvatar ??
      credentials?.facebookAvatar
  );

  const [currentLogo, setCurrentLogo] = useState(companyLogo);
  const [isSkip, setIsSkip] = useState(false);
  const avatarRef = useRef(null);
  const logoRef = useRef(null);

  const sizeLimit = 2 * 1048576;

  useEffect(() => {
    if (selectedPlan.name === "Business") return;

    if (currentPlan && !info.company) {
      goForward();
    }
  }, []);

  const handleSkipProcess = async () => {
    const data = await toPng(avatarRef.current);
    addInformation({ avatar: data });
    goForward();
  };

  const getImage = () => {
    if (currentAvatar) {
      const _avatar =
        typeof currentAvatar === "string"
          ? currentAvatar
          : URL.createObjectURL(currentAvatar);
      return <img height={120} width={120} alt="preview image" src={_avatar} />;
    }
    return (
      <div ref={avatarRef} className="onboard__form-avatar__image-content">
        <div>{`${info.firstname[0]}${info.lastname[0]}`}</div>
      </div>
    );
  };

  const getCompanyLogo = () => {
    if (currentLogo) {
      const _avatar =
        typeof currentLogo === "string"
          ? currentLogo
          : URL.createObjectURL(currentLogo);
      return <img height={120} width={120} alt="preview image" src={_avatar} />;
    }
    return (
      <div ref={logoRef} className="onboard__form-avatar__image-content">
        <div>{`${info.company[0]}`}</div>
      </div>
    );
  };

  const handleSubmit = async () => {
    // * Changing plan. so only company logo can be uploaded
    if (currentPlan) {
      addInformation({
        companyLogo: currentLogo ?? (await toPng(logoRef.current)),
      });
      goForward();

      return;
    }

    const _avatar = currentAvatar ?? (await toPng(avatarRef.current));

    addInformation({
      avatar: _avatar,
      companyLogo: info.company
        ? currentLogo ?? toPng(logoRef.current)
        : undefined,
    });
    goForward();
  };

  return (
    <div className="onboard-avatar">
      <h2 className="onboard__header">
        {Translation("Upload your profile picture")}
      </h2>
      <Formik
        initialValues={{
          avatar,
        }}
        initialErrors={avatar ? {} : { avatar: "" }}
        onSubmit={handleSubmit}
      >
        {({
          errors: { avatar: avatarError, companyLogo: logoError },
          isValid,
          setFieldValue,
          setFieldError,
        }) => {
          const handleFileChange = (files) => {
            if (files.length === 0) return;
            if (files[0].size > sizeLimit) {
              setFieldError("avatar", Translation("File size over limit!"));
              return;
            }

            setCurrentAvatar(files[0]);
            setFieldValue("avatar", files[0]);
          };

          const handleLogoFileChange = (files) => {
            if (files.length === 0) return;
            if (files[0].size > sizeLimit) {
              setFieldError(
                "companyLogo",
                Translation("File size over limit!")
              );
              return;
            }

            setCurrentLogo(files[0]);
            setFieldValue("companyLogo", files[0]);
          };

          return (
            <Form className="onboard__form onboard__form-avatar">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: info.company ? "1fr 1fr" : "1fr",
                }}
              >
                {/* {!currentPlan && (
                )} */}
                <div className="onboard__form-content">
                  {info.company && (
                    <h3 style={{ fontWeight: 500, color: "#555" }}>Profile</h3>
                  )}
                  <div className="onboard__form-avatar__image">
                    {getImage()}
                  </div>
                  <FileUploader
                    id="avatar"
                    leadingIcon={<UploadSvg />}
                    label={Translation("Upload image")}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {avatarError && avatarError !== "Missing avatar" && (
                    <span className="onboard__form-avatar__error">
                      {avatarError}
                    </span>
                  )}
                  <span className="onboard__form-avatar__restriction">
                    {Translation("Max image file size of 2MB")}
                  </span>
                </div>
                <div
                  className="onboard__form-content"
                  style={{ gridColumn: currentPlan ? "1 / 3" : 2 }}
                >
                  {info.company && (
                    <>
                      <h3 style={{ fontWeight: 500, color: "#555" }}>
                        Company Logo
                      </h3>
                      <div className="onboard__form-avatar__image">
                        {getCompanyLogo()}
                      </div>
                      <FileUploader
                        id="companyLogo"
                        leadingIcon={<UploadSvg />}
                        label={Translation("Upload image")}
                        accept="image/*"
                        onChange={handleLogoFileChange}
                      />
                      {logoError && logoError !== "Missing avatar" && (
                        <span className="onboard__form-avatar__error">
                          {logoError}
                        </span>
                      )}
                      <span className="onboard__form-avatar__restriction">
                        {Translation("Max image file size of 2MB")}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="onboard__form-cta">
                <Button
                  label={TranslationCommon("Return")}
                  leadingIcon={<ArrowLeftSvg />}
                  onClick={goBack}
                  variant="secondary"
                />
                <div className="onboard__form-cta__divider" />
                <Button
                  type="submit"
                  label={TranslationCommon("Continue")}
                  trailingIcon={<ArrowRightSvg />}
                />
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

Avatar.propTypes = {};

export default Avatar;
